import { toArray } from '@shared/utils/array';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import useQuery from '@/util/useQuery';
import { SelectionContext } from './SelectionContext';
import { emitSelectEvent } from './useSelectionEvents';

interface MemorySelectionProviderProps {
  onSelect?: (ids: string[]) => void;
  onClearSelection?: () => void;
  children: ReactNode;
  domain?: string;
}

export const MemorySelectionProvider = ({
  onSelect,
  onClearSelection,
  children,
  domain,
}: MemorySelectionProviderProps) => {
  const { query } = useQuery();
  const initialSelected = useMemo(
    () => (query.selected ? query.selected.split(',') : []),
    [query.selected]
  );
  const [selected, setSelected] = useState<string[]>(initialSelected);

  const select = useCallback(
    (id: string | string[]) => {
      const ids = toArray(id);
      emitSelectEvent({ domain, selectedIds: ids });
      setSelected(ids);
      onSelect?.(ids);
    },
    [onSelect, domain]
  );

  const toggleSelected = useCallback(
    (id: string) => {
      const ids = selected.includes(id)
        ? selected.filter((i) => i !== id)
        : [...selected, id];
      setSelected(ids);
    },
    [selected]
  );

  const clearSelection = useCallback(() => {
    setSelected([]);
    onClearSelection?.();
  }, [onClearSelection]);

  return (
    <SelectionContext.Provider
      value={{
        select,
        toggleSelected,
        clearSelection,
        selected,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
};
